import React from "react";
import { FiSettings, FiCheckCircle, FiPlusCircle, FiEdit2 } from "react-icons/fi";

const NextStepsSection = ({
  openDropdown,
  toggleDropdown,
  setShowOptOutModal,
  setShowProductsModal,
  setShowListMessageModal,
  setShowCustomReplyModal,
  setShowWorkflowModal,
  optOutConfig,
  productsConfig,
  listMessageConfig,
  customReplyConfig,
  workflowConfig
}) => {
  const actionItems = [
    {
      id: 1,
      title: "Opt out the customer",
      description: "Auto update the WhatsApp Opted in trait to false",
      icon: "🚫",
      config: optOutConfig,
      action: () => setShowOptOutModal(true),
      color: "danger",
      configuredText: optOutConfig ? "Opt-out configured" : null
    },
    {
      id: 2,
      title: "Send Your Products",
      description: "Auto-send your collection list/catalog",
      icon: "🛍️",
      config: productsConfig,
      action: () => setShowProductsModal(true),
      color: "primary",
      configuredText: productsConfig ? `${productsConfig.products?.length || 0} products selected` : null
    },
    {
      id: 3,
      title: "Send Interactive List Message",
      description: "Auto-send the list of FAQs set up here",
      icon: "📋",
      config: listMessageConfig,
      action: () => setShowListMessageModal(true),
      color: "info",
      configuredText: listMessageConfig ? "List message configured" : null
    },
    {
      id: 4,
      title: "Send a Custom Reply",
      description: "Auto-send custom reply message",
      icon: "💬",
      config: customReplyConfig,
      action: () => setShowCustomReplyModal(true),
      color: "success",
      configuredText: customReplyConfig ? "Custom reply set" : null
    },
    {
      id: 5,
      title: "Send a Workflow",
      description: "Auto-send a predefined workflow",
      icon: "⚙️",
      config: workflowConfig,
      action: () => setShowWorkflowModal(true),
      color: "warning",
      configuredText: workflowConfig ? "Workflow configured" : null
    }
  ];

  const configuredCount = actionItems.filter(item => item.config).length;
  const progressPercentage = (configuredCount / actionItems.length) * 100;

  return (
    <div className="card mb-4 border-0 shadow-sm">
      <div 
        className="card-header bg-white d-flex justify-content-between align-items-center py-3" 
        onClick={toggleDropdown} 
        style={{ cursor: "pointer" }}
      >
        <div className="d-flex align-items-center">
          <div 
            className="step-number bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{ width: "32px", height: "32px", fontSize: "14px", fontWeight: "600" }}
          >
            3
          </div>
          <div>
            <h5 className="mb-0">Customer Response Actions</h5>
            <small className="text-muted">
              Configure automated responses when customers reply to your message
            </small>
          </div>
        </div>
        <div className="d-flex align-items-center">
          {configuredCount > 0 && (
            <span className="badge bg-success me-2">
              {configuredCount} configured
            </span>
          )}
          <span className="text-muted fs-5">
            {openDropdown ? "▲" : "▼"}
          </span>
        </div>
      </div>
      
      {openDropdown && (
        <div className="card-body pt-3">
          <div className="mb-4">
            <div className="alert alert-info d-flex align-items-center">
              <i className="bi bi-info-circle me-2 fs-5"></i>
              <div>
                <strong>Automate customer interactions</strong> - These actions will trigger automatically 
                when customers reply to your campaign message
              </div>
            </div>
          </div>

          {/* Progress indicator */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Configuration Progress</h6>
              <small className="text-muted">{configuredCount} of {actionItems.length} actions configured</small>
            </div>
            <div className="progress" style={{ height: "8px", borderRadius: "4px" }}>
              <div 
                className="progress-bar" 
                role="progressbar" 
                style={{ 
                  width: `${progressPercentage}%`,
                  backgroundColor: progressPercentage === 100 ? '#198754' : '#0d6efd'
                }}
                aria-valuenow={progressPercentage} 
                aria-valuemin="0" 
                aria-valuemax="100"
              ></div>
            </div>
          </div>

          <div className="row g-3">
            {actionItems.map((item) => (
              <div key={item.id} className="col-md-6">
                <div className={`card h-100 border-0 shadow-sm ${item.config ? 'border-start border-3 border-' + item.color : ''}`}>
                  <div className="card-body">
                    <div className="d-flex align-items-start mb-3">
                      <span className="me-3 fs-3">{item.icon}</span>
                      <div className="flex-grow-1">
                        <h6 className="card-title mb-1 fw-semibold">{item.title}</h6>
                        <p className="card-text text-muted small mb-2">{item.description}</p>
                      </div>
                    </div>
                    
                    {item.config && item.configuredText && (
                      <div className="mb-3">
                        <div className="d-flex align-items-center bg-light rounded p-2">
                          <FiCheckCircle className={`text-${item.color} me-2`} />
                          <small className="text-muted">{item.configuredText}</small>
                        </div>
                      </div>
                    )}
                    
                    <button
                      className={`btn btn-sm w-100 ${item.config ? 'btn-outline-' + item.color : 'btn-' + item.color}`}
                      onClick={item.action}
                    >
                      {item.config ? (
                        <>
                          <FiEdit2 className="me-1" /> Edit Configuration
                        </>
                      ) : (
                        <>
                          <FiPlusCircle className="me-1" /> Configure Action
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-3 border-top">
            <div className="row">
              <div className="col-md-8">
                <h6 className="mb-2">How Response Actions Work</h6>
                <ul className="text-muted small ps-3">
                  <li>Actions are triggered when customers reply to your campaign message</li>
                  <li>Multiple actions can be configured to work together</li>
                  <li>Actions execute in the order they are configured</li>
                  <li>You can test actions before going live with your campaign</li>
                </ul>
              </div>
              <div className="col-md-4 text-md-end">
                <button className="btn btn-outline-secondary me-2">
                  <i className="bi bi-question-circle me-1"></i> Help
                </button>
                <button className="btn btn-outline-primary">
                  <i className="bi bi-lightning me-1"></i> Test Actions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NextStepsSection;